import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import {
  AffiliationType,
  AffiliationTypeOnServerCreated,
  selectAffiliationTypesActionLoading,
} from '../../../../../../../core/lists';
import { AppState } from '../../../../../../../core/reducers';
import { AffiliationTypeOnServerUpdated } from '../../../../../../../core/lists/_actions/affiliation-type.actions';
import { selectAffiliationTypeErrorState } from '../../../../../../../core/lists/_selectors/affiliation-type.selectors';

@Component({
  selector: 'kt-affiliation-type-edit',
  templateUrl: './affiliation-type-edit.component.html',
})
export class AffiliationTypeEditComponent implements OnInit, OnDestroy {
  affiliationType: AffiliationType;
  affiliationTypeEditForm: FormGroup;
  hasFormErrors = false;
  hasSubmissionErrors = false;
  submissionErrorObj: any;
  viewLoading = true;
  loadingAfterSubmit = false;

  private componentSubscriptions: Subscription;

  constructor(
    public dialogRef: MatDialogRef<AffiliationTypeEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private store: Store<AppState>,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.affiliationType = this.data.affiliationType;
    this.store
      .pipe(select(selectAffiliationTypesActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.createForm();
  }

  ngOnDestroy() {
    if (this.componentSubscriptions) {
      this.componentSubscriptions.unsubscribe();
    }
  }

  createForm() {
    this.affiliationTypeEditForm = this.fb.group({
      affiliation_type_description: [
        this.affiliationType.affiliation_type_description,
        Validators.required,
      ],
      affiliation_type_name: [this.affiliationType.affiliation_type_name, Validators.required],
    });
  }

  prepareAffiliationType(): AffiliationType {
    const controls = this.affiliationTypeEditForm.controls;
    const _affiliationType = new AffiliationType();
    _affiliationType.clear();
    _affiliationType.affiliation_type_id = this.affiliationType.affiliation_type_id;
    _affiliationType.affiliation_type_name = controls.affiliation_type_name.value;
    _affiliationType.affiliation_type_description = controls.affiliation_type_description.value;
    for (const [key, value] of Object.entries(_affiliationType)) {
      if (typeof value === 'string') {
        _affiliationType[key] = value.trim();
      }
    }
    return _affiliationType;
  }

  onSubmit() {
    this.hasFormErrors = false;
    this.hasSubmissionErrors = false;
    const controls = this.affiliationTypeEditForm.controls;
    if (this.affiliationTypeEditForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }

    const editedAffiliationType = this.prepareAffiliationType();
    if (editedAffiliationType.affiliation_type_id !== 0) {
      this.updateAffiliationType(editedAffiliationType);
    } else {
      this.createAffiliationType(editedAffiliationType);
    }
  }

  updateAffiliationType(_affiliationType: AffiliationType) {
    this.store.dispatch(
      new AffiliationTypeOnServerUpdated({
        affiliationType: _affiliationType,
      }),
    );
    this.store.pipe(select(selectAffiliationTypesActionLoading)).subscribe((res) => {
      if (res !== true) {
        this.store.pipe(select(selectAffiliationTypeErrorState)).subscribe((error) => {
          if (error) {
            if (error.error.errors) {
              this.submissionErrorObj = error.error.errors;
            } else {
              this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
            }
            this.hasSubmissionErrors = true;
          } else {
            this.dialogRef.close({ _affiliationType, isEdit: true });
          }
        });
      }
    });
  }

  createAffiliationType(_affiliationType: AffiliationType) {
    this.store.dispatch(new AffiliationTypeOnServerCreated({ affiliationType: _affiliationType }));
    this.store.pipe(select(selectAffiliationTypesActionLoading)).subscribe((res) => {
      if (res !== true) {
        this.store.pipe(select(selectAffiliationTypeErrorState)).subscribe((error) => {
          if (error) {
            if (error.error.errors) {
              this.submissionErrorObj = error.error.errors;
            } else {
              this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
            }
            this.hasSubmissionErrors = true;
          } else {
            this.dialogRef.close({ _affiliationType, isEdit: false });
          }
        });
      }
    });
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }

  getTitle() {
    let result = this.translate.instant('AFFILIATION.NEW_AFFILIATION_TYPE');
    if (!this.affiliationType.affiliation_type_id) {
      return result;
    }
    result =
      this.translate.instant('AFFILIATION.EDIT_AFFILIATION_TYPE') +
      `-` +
      `${this.affiliationType.affiliation_type_id}`;
    return result;
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.affiliationTypeEditForm.controls[controlName];
    if (!control) {
      return false;
    }
    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }
}
